import { useWatch } from 'react-hook-form';
import useTranslation from '~/hooks/useTranslation';
import FormGroup from '~/components/Form/FormGroup';
import { useCountryOptions } from '~/hooks/bookingFlow/useCountryOptions';
import TextField from '~/components/Form/TextField';
import useStateOptions from '~/hooks/bookingFlow/useStateOptions';
import addressStyles from '~/styles/bookingFlow/address.module.css';
import SelectField from '~/components/Form/SelectField';
import InformationCard from './InformationCard';

type Props = {
  registerFieldPath: string;
  className?: string;
};

export default function Address({
  registerFieldPath,
  className,
}: Props) {
  const translate = useTranslation();
  const countryOptions = useCountryOptions();
  const stateOptions = useStateOptions();

  const country = useWatch({ name: `${registerFieldPath}.country` });

  const hasDoubleAddressLine = ['gb', 'uk'].indexOf(country) >= 0;
  const hasState = ['us'].indexOf(country) >= 0;

  return (
    <InformationCard
      title={translate('bookingFlow.information.address_title')}
      className={className}
    >
      <FormGroup>
        <SelectField
          label={translate('form.country')}
          registerField={`${registerFieldPath}.country`}
          optionGroups={countryOptions}
          required
          allowEmpty
        />
      </FormGroup>

      <div className={addressStyles.addressFields}>
        <FormGroup>
          <TextField
            label={translate('form.street')}
            registerField={`${registerFieldPath}.street`}
            required
          />
        </FormGroup>

        <FormGroup>
          <TextField
            label={translate('form.housenum')}
            registerField={`${registerFieldPath}.housenum`}
            required
          />
        </FormGroup>
      </div>

      {hasDoubleAddressLine && (
        <FormGroup>
          <TextField
            label={translate('form.address_line_2')}
            registerField={`${registerFieldPath}.address_line_2`}
            required
          />
        </FormGroup>
      )}

      {hasState && (
        <FormGroup>
          <SelectField
            label={translate('form.state')}
            registerField={`${registerFieldPath}.state`}
            required
            options={stateOptions}
          />
        </FormGroup>
      )}

      <FormGroup>
        <TextField
          label={translate('form.postal_code')}
          registerField={`${registerFieldPath}.postal_code`}
          required
        />
      </FormGroup>

      <FormGroup>
        <TextField
          label={translate('form.city')}
          registerField={`${registerFieldPath}.city`}
          required
        />
      </FormGroup>
    </InformationCard>
  );
}
